/**
 * lib/viewerTelemetry.js — Iter 63.1 Phase 11.
 *
 * Internal-only, no customer-facing dashboard. Tracks which Viewer
 * controls field users actually use so future iterations can stop
 * guessing.
 *
 * Design:
 *   - Fire-and-forget. Never throws into the UI.
 *   - Queued + flushed in batches (every 10 events OR 5 seconds OR
 *     before navigation/unload).
 *   - localStorage fallback when offline; drained on next online flush.
 *   - Endpoint: POST /api/viewer/events with `{ events: [...] }`.
 */
import { api } from "@/lib/api";

const QUEUE_KEY = "fop_viewer_telemetry_queue_v1";
const BATCH_SIZE = 10;
const FLUSH_INTERVAL_MS = 5000;
const MAX_QUEUE = 500; // safety cap

let inMem = [];
let timer = null;

function loadPersisted() {
  try {
    const raw = window.localStorage.getItem(QUEUE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function persist(arr) {
  try {
    window.localStorage.setItem(QUEUE_KEY, JSON.stringify(arr.slice(-MAX_QUEUE)));
  } catch {
    /* quota / private mode — ignore */
  }
}

function scheduleFlush() {
  if (timer) return;
  timer = setTimeout(() => {
    timer = null;
    flush();
  }, FLUSH_INTERVAL_MS);
}

async function flush() {
  const persisted = loadPersisted();
  const batch = [...persisted, ...inMem].slice(0, MAX_QUEUE);
  if (batch.length === 0) return;
  inMem = [];
  try {
    await api.post("/viewer/events", { events: batch });
    // Success — drop persisted copy.
    persist([]);
  } catch {
    // Offline or 4xx — push back to localStorage; will retry next batch.
    persist(batch);
  }
}

/** Public — record one event. `props` is small JSON-safe metadata. */
export function trackViewerEvent(name, props = {}) {
  if (!name) return;
  try {
    inMem.push({
      event: String(name).slice(0, 80),
      props: props || {},
      ts: Date.now(),
      sheet_id: props.sheet_id || null,
    });
    if (inMem.length >= BATCH_SIZE) {
      flush();
    } else {
      scheduleFlush();
    }
  } catch {
    /* swallow */
  }
}

// Drain on navigation / unload so we don't lose the last clicks.
if (typeof window !== "undefined") {
  window.addEventListener("pagehide", () => {
    try {
      const persisted = loadPersisted();
      const batch = [...persisted, ...inMem];
      if (batch.length === 0) return;
      // sendBeacon is fire-and-forget and survives unload.
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/viewer/events`;
      const token = window.localStorage.getItem("fop_bearer");
      if (navigator.sendBeacon && token) {
        // sendBeacon can't send Authorization header — fall back to the
        // token-in-querystring trick that the exports route already
        // uses if a future endpoint accepts it. For V1 we just persist
        // — backend will catch up on the next online flush.
      }
      persist(batch);
    } catch {}
  });
}

export default trackViewerEvent;
